import React, {
  Fragment,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, NavLink } from "react-router-dom";
import {
  Drawer,
  IconButton,
  ListItemText,
  ListItemIcon,
  ListItem,
  List,
  Collapse,
  Typography,
  Box,
  Tooltip,
  Fade,
  CircularProgress,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import clsx from "clsx";
import sidebar from "../../../config/sidebar.json";
import { enabledPermissions } from "../../../services/enabledPermissions";
import IconRenderer from "../../IconRenderer";
import { connect } from "react-redux";

const SidebarNavigationItems = React.memo((props) => {
  const useSiderStatus = sessionStorage.siderbarO === "true";
  const [openSidebar, setOpenSidebar] = useState(useSiderStatus);
  const [enabledSidebarItems, setEnabledSidebarItems] = useState([]);
  const [previousSidebarItems, setPreviousSidebarItems] = useState([]);
  const [collapsedStates, setCollapsedStates] = useState({});
  const [initialLoad, setInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccountChanging, setIsAccountChanging] = useState(false);
  const [skeletonCount, setSkeletonCount] = useState(5);

  const location = useLocation();
  const headersSetCookie = localStorage.getItem("headersSetCookie");
  const { resultsUserPermissions } = props;
  const initializedOnce = useRef(false);
  const previousLocation = useRef(location.pathname);
  const currentUserContext = useRef(null);
  const currentAccountUuid = useRef(null);
  const loadingTimeoutRef = useRef(null);

  const detectContextChange = useCallback(() => {
    const userData = localStorage.getItem("userData");
    const activeCount = localStorage.getItem("uuidSetCookie");
    const userChanged = currentUserContext.current !== userData;
    const accountChanged = currentAccountUuid.current !== activeCount;

    if (userChanged || accountChanged) {
      currentUserContext.current = userData;
      currentAccountUuid.current = activeCount;
      return true;
    }
    return false;
  }, []);

  const resetSidebarState = useCallback(() => {
    setPreviousSidebarItems(enabledSidebarItems);
    setIsAccountChanging(true);
    setIsLoading(true);

    setCollapsedStates({});
    setInitialLoad(true);
    initializedOnce.current = false;

    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    loadingTimeoutRef.current = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setIsAccountChanging(false);
        console.warn("Sidebar loading timeout - forcing completion");
      }
    }, 5000);
  }, [enabledSidebarItems, isLoading]);

  const [accountUid, setAccountUid] = useState(localStorage.getItem("accountUid"));
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem("isAdmin") === "true");
  const [isOverview, setIsOverview] = useState(localStorage.getItem("isOverview") === "true");

  useEffect(() => {
    setAccountUid(localStorage.getItem("accountUid"));
    setIsAdmin(localStorage.getItem("isAdmin") === "true");
    setIsOverview(localStorage.getItem("isOverview") === "true");
  }, [location.pathname]);

  useEffect(() => {
    const handleHotelChanged = (event) => {
      if (!event?.detail?.title) return;
      const nextAccountUid = localStorage.getItem("accountUid");
      if (!nextAccountUid) return;
      setAccountUid(nextAccountUid);
      setIsAdmin(localStorage.getItem("isAdmin") === "true");
      setIsOverview(localStorage.getItem("isOverview") === "true");
    };
    window.addEventListener("hotelChanged", handleHotelChanged);
    return () => window.removeEventListener("hotelChanged", handleHotelChanged);
  }, []);

  const withAccountUid = useCallback(
    (routing) => {
      if (!routing) return "#";
      const routeWithoutDashboard = routing.replace(/^dashboard\/?/, '');
      if (isAdmin) return `/dashboard/admin${routeWithoutDashboard ? `/${routeWithoutDashboard}` : ''}`;
      if (isOverview) return `/dashboard/overview${routeWithoutDashboard ? `/${routeWithoutDashboard}` : ''}`;
      if (accountUid) return `/dashboard/${accountUid}${routeWithoutDashboard ? `/${routeWithoutDashboard}` : ''}`;
      return `/dashboard${routeWithoutDashboard ? `/${routeWithoutDashboard}` : ''}`;
    },
    [accountUid, isAdmin, isOverview]
  );

  const isActiveWithAccountUid = useCallback(
    (routing) => {
      if (!routing || initialLoad) return false;
      const current = location.pathname;
      const target = withAccountUid(routing);
      
      if (current === target) return true;
      
      const routeWithoutDashboard = routing.replace(/^dashboard\/?/, '');
      if (!routeWithoutDashboard || routeWithoutDashboard === '') {
        return false;
      }
      
      return current.startsWith(`${target}/`);
    },
    [location.pathname, initialLoad, withAccountUid]
  );


  const initializeCollapsedStates = useCallback(
    (items) => {
      const initialStates = {};
      const processItems = (items) => {
        items.forEach((item) => {
          if (Array.isArray(item.submenu) && item.submenu.length > 0) {
            const hasActiveSubmenu = item.submenu.some((subItem) => {
              if (!subItem.routing) return false;
              const current = location.pathname;
              const target = withAccountUid(subItem.routing);
              return current === target || current.startsWith(`${target}/`);
            });
            initialStates[item.id] = hasActiveSubmenu;
            processItems(item.submenu);
          }
        });
      };
      processItems(items);
      return initialStates;
    },
    [location.pathname]
  );

  const isActiveRoute = useCallback(
    (routing) => {
      return isActiveWithAccountUid(routing);
    },
     [isActiveWithAccountUid]
  );

  useEffect(() => {
    if (detectContextChange()) {
      resetSidebarState();
    }
  }, [detectContextChange, resetSidebarState]);

  useEffect(() => {
    const userData = localStorage.getItem("userData");
    if (userData) {
      try {
        const idUser = JSON.parse(userData)?.id;
        if (idUser) {
          setIsLoading(true);
          const savedSidebarCount = localStorage.getItem("sidebarItemCount");
          if (savedSidebarCount) setSkeletonCount(Number(savedSidebarCount));
          props.getUserPermissions(headersSetCookie, `${idUser}`, idUser);
        }
      } catch (error) {
        console.error("Error parsing userData:", error);
        setIsLoading(false);
        setIsAccountChanging(false);
      }
    }
  }, [headersSetCookie, props.getUserPermissions]);

  useEffect(() => {
    if (resultsUserPermissions?.uuid) {
      const { roles, permissionsByAccount } = resultsUserPermissions;
      const isSuperUser = roles.includes("ROLE_SUPERUSER");
      const hasGlobalAdmin = roles.includes("GLOBAL_ADMIN");
      const activeCount = localStorage.getItem("uuidSetCookie");

      const activePermission = Array.isArray(permissionsByAccount)
        ? permissionsByAccount.find((item) => item.accountUuid === activeCount)
        : null;


      const enabledItems = enabledPermissions(
        props.sidebar,
        activePermission?.permissions?.modules || [],
        hasGlobalAdmin,
        isSuperUser
      );

      setEnabledSidebarItems(enabledItems);
      setCollapsedStates(initializeCollapsedStates(enabledItems));
      localStorage.setItem("sidebarItemCount", enabledItems.length.toString());

      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }

      setTimeout(() => {
        setIsLoading(false);
        setIsAccountChanging(false);
        setInitialLoad(false);
        setPreviousSidebarItems([]);
        initializedOnce.current = true;
      }, 400);
    }
  }, [resultsUserPermissions, props.sidebar, initializeCollapsedStates]);

  useEffect(() => {
    if (!isLoading && enabledSidebarItems.length > 0) {
      const newCollapsedStates = { ...collapsedStates };
      let hasChanges = false;

      enabledSidebarItems.forEach((item) => {
        if (item.submenu && Array.isArray(item.submenu)) {
          const hasActiveSubmenu = item.submenu.some(
            (subItem) => subItem.routing && isActiveRoute(subItem.routing)
          );
          if (hasActiveSubmenu && !newCollapsedStates[item.id]) {
            newCollapsedStates[item.id] = true;
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        setCollapsedStates(newCollapsedStates);
      }
    }
  }, [
    location.pathname,
    isLoading,
    enabledSidebarItems,
    isActiveRoute,
    collapsedStates,
  ]);

  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  const handleDrawerClose = useCallback(() => {
    setOpenSidebar((prev) => {
      sessionStorage.setItem("siderbarO", !prev);
      return !prev;
    });
  }, []);

  const toggleCollapse = useCallback((id, event) => {
    // Prevent event bubbling to avoid closing sidebar
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setCollapsedStates((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const SidebarItem = React.memo(({ item }) => {
    const {
      listItemText: { primary },
      id,
      group,
      submenu,
      routing,
      icon,
      onClick: onClickProp,
      disabled,
      tooltip,
    } = item;

    const isCollapsed = collapsedStates[id] || false;
    const isCurrentActive = useMemo(
      () => isActiveRoute(routing),
      [routing, isActiveRoute]
    );

    const handleSubmenuToggle = useCallback(
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        toggleCollapse(id, event);

        if (onClickProp) {
          onClickProp(event, item);
        }
      },
      [toggleCollapse, id, onClickProp, item]
    );

    const handleNavigationClick = useCallback(
      (event) => {
        if (onClickProp) {
          onClickProp(event, item);
        }
      },
      [onClickProp, item]
    );

    const listItemContent = (
      <>
        <ListItemIcon>
          <IconRenderer icon={icon} />
        </ListItemIcon>
        <ListItemText primary={primary} disableTypography />
        <Typography variant="body2">
          {Array.isArray(submenu) && submenu.length ? (
            isCollapsed ? (
              <ExpandLess />
            ) : (
              <ExpandMore />
            )
          ) : null}
        </Typography>
      </>
    );

    const baseClasses = `sidebarListItem ${openSidebar ? "expanded" : "collapsed"
      }`;

    const listItemElement = (() => {
      if (Array.isArray(submenu) && submenu.length > 0) {
        return (
          <ListItem
            button
            onClick={handleSubmenuToggle}
            className={baseClasses}
            disabled={disabled}
          >
            {listItemContent}
          </ListItem>
        );
      } else if (routing) {
        return (
          <ListItem
            button
            component={disabled ? "div" : NavLink}
            to={disabled ? undefined : withAccountUid(routing)}
            selected={isCurrentActive}
            className={baseClasses}
            disabled={disabled}
            onClick={handleNavigationClick}
          >
            {listItemContent}
          </ListItem>
        );
      } else {
        return (
          <ListItem
            button
            onClick={handleNavigationClick}
            className={baseClasses}
            disabled={disabled}
          >
            {listItemContent}
          </ListItem>
        );
      }
    })();

    return (
      <Fragment>
        {disabled ? (
          <Tooltip title={tooltip || "Coming Soon"} placement="right" arrow>
            <span>{listItemElement}</span>
          </Tooltip>
        ) : (
          listItemElement
        )}
        {Array.isArray(submenu) && (
          <Collapse
            in={isCollapsed}
            timeout="auto"
            unmountOnExit
            className="nested"
          >
            <List
              component="div"
              disablePadding
              className={`subMenu ${openSidebar ? "expanded" : "collapsed"}`}
            >
              {submenu.map((subItem, index) => (
                <SidebarItem key={subItem.id || index} item={subItem} />
              ))}
            </List>
          </Collapse>
        )}
      </Fragment>
    );
  });

  const itemsToShow = useMemo(() => {
    if (isAccountChanging && previousSidebarItems.length > 0) {
      return previousSidebarItems;
    }
    return enabledSidebarItems;
  }, [isAccountChanging, previousSidebarItems, enabledSidebarItems]);

  const sidebarItemsList = useMemo(
    () =>
      itemsToShow.map((item, i) => (
        <SidebarItem item={item} key={item.id || i} />
      )),
    [itemsToShow, collapsedStates, openSidebar, isActiveRoute]
  );
  const shouldShowLoading = isLoading || isAccountChanging;

  return (
    <Fragment>
      {shouldShowLoading && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(245, 245, 245, 0.6)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              zIndex: 1300,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: "20px",
              zIndex: 1400,
              pointerEvents: "none",
            }}
          >
            <CircularProgress
              size={60}
              thickness={4}
              style={{ color: "#135265" }}
            />
            <Typography variant="h6" style={{ color: "#424242" }}>
              Loading Travel Curious...
            </Typography>
          </div>
        </>
      )}
      <Drawer className="drawerContainer" variant="permanent"
        classes={{ paper: clsx("drawerPaper", !openSidebar && "drawerPaperClose"), }}
        open={openSidebar}
      >
        <List component="nav" className="sidebarnav-list sidebarMenuContainer" style={{ direction: "ltr" }}>
          {itemsToShow.length > 0 ? (
            <Fade in={!isAccountChanging} timeout={400}>
              <div>
                {sidebarItemsList}
              </div>
            </Fade>
          ) : !shouldShowLoading ? (
            <Box sx={{ padding: "16px", textAlign: "center" }}>
              <Typography variant="body2" color="textSecondary">
                No menu items available
              </Typography>
            </Box>
          ) : null}
        </List>
        {!shouldShowLoading && (
          <IconButton className="toolbarDrawerButton" onClick={handleDrawerClose}>
            {!openSidebar ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}
      </Drawer>
    </Fragment>
  );
});

SidebarNavigationItems.displayName = "SidebarNavigationItems";

const mapStateToProps = (reducers) => reducers.usersReducer;

const mapDispatchToProps = (dispatch) => ({
  getUserPermissions: (access, query, idUser) =>
    dispatch({ type: "GET_USER_PERMISSIONS", access, query, idUser }),
});

const ConnectedSidebarNavigationItems = connect(
  mapStateToProps,
  mapDispatchToProps
)(SidebarNavigationItems);

export default function SidebarNavigation() {
  return <ConnectedSidebarNavigationItems sidebar={sidebar} />;
}
